import React, { useState, useEffect } from 'react';
import { Form, message } from 'antd';
import RecognitionSettingsPresentation from '../../presentation/RecognitionSettingsPresentation';
import { snappy } from 'src/_shared';
import { withApollo } from 'react-apollo';
import { gql, useQuery } from '@apollo/client';
import { Spinner } from 'src/_shared';
import { withGetCompany } from 'src/_shared/api/components/settings/';
import { connect } from 'react-redux';
import { userActions } from 'src/actions.js';
import { useSelector } from 'react-redux';

const CreateSnappyGrant = gql`
	mutation CreateSnappyGrant($input: SnappyGrantInput!) {
		createSnappyGrant(input: $input) {
			id
			name
			description
			externalSnappyId
			companyId
			snappyCampaignId
			snappyCampaignTierId
			active
			allowOverBudget
			allowMultipleGrants
			allowForNominations
			allowRevoke
			requireApproval
			allowAssignmentNoGift
			grantCategory
			costInCurrency
			currencyIsoCode
			currencySymbol
			giftExpiryDays
			remainingAssignments
			totalAssignments
			badgeIds
			badgeAssignmentIds
			snappyGiftIds
			isHidden
			isArchived
			isMirroredSnappyCampaignTier
			externalInformation
			redemptionLogs
			createdBy
			createdAt
		}
	}
`;

const UpdateSnappyGrant = gql`
	mutation UpdateSnappyGrant($input: SnappyGrantInput!) {
		updateSnappyGrant(input: $input) {
			id
			name
			description
			externalSnappyId
			companyId
			snappyCampaignId
			snappyCampaignTierId
			active
			allowOverBudget
			allowMultipleGrants
			allowForNominations
			allowRevoke
			requireApproval
			allowAssignmentNoGift
			grantCategory
			costInCurrency
			currencyIsoCode
			currencySymbol
			giftExpiryDays
			remainingAssignments
			totalAssignments
			badgeIds
			badgeAssignmentIds
			snappyGiftIds
			isHidden
			isArchived
			isMirroredSnappyCampaignTier
			externalInformation
			redemptionLogs
			updatedBy
			updatedAt
		}
	}
`;

const QuerySnappyGrantsByCompanyId = gql`
	query QuerySnappyGrantsByCompanyId(
		$companyId: ID!
		$limit: Int
		$nextToken: String
	) {
		querySnappyGrantsByCompanyId(
			companyId: $companyId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				id
				name
				description
				externalSnappyId
				companyId
				snappyCampaignId
				snappyCampaignTierId
				active
				allowOverBudget
				allowMultipleGrants
				allowForNominations
				allowRevoke
				requireApproval
				allowAssignmentNoGift
				grantCategory
				costInCurrency
				currencyIsoCode
				currencySymbol
				giftExpiryDays
				remainingAssignments
				totalAssignments
				badgeIds
				badgeAssignmentIds
				snappyGiftIds
				isHidden
				isArchived
				isMirroredSnappyCampaignTier
				externalInformation
				redemptionLogs
				createdBy
				createdAt
				updatedBy
				updatedAt
			}
			nextToken
		}
	}
`;

const QuerySnappyApiKeys = gql`
	query QuerySnappyApiKeys($companyId: ID!) {
		querySnappyApiKeys(companyId: $companyId) {
			createdAt
			companyId
			expirationDate
			enforceMtls
			id
			name
		}
	}
`;

const GetSnappyAPIKey = gql`
	query GetSnappyAPIKey($companyId: ID!) {
		getSnappyAPIKey(companyId: $companyId) {
			createdAt
			keyId
			label
			last4
			status
			maskedKey
			lastVerifiedAt
		}
	}
`;

const DeleteSnappyGrant = gql`
	mutation DeleteSnappyGrant($id: ID!) {
		deleteSnappyGrant(id: $id) {
			id
			name
			description
			externalSnappyId
			snappyCampaignId
		}
	}
`;

function RecognitionSettingsContainer(props) {
	const { onUpdateCompany, updateCurrentUserCompany } = props;
	const [campaigns, setCampaigns] = useState([]);
	const [snappyCampaigns, setSnappyCampaigns] = useState([]);
	const [showCreateCampaignForm, setShowCreateCampaignForm] = useState(false);
	const [snappyGrants, setSnappyGrants] = useState([]);
	const [form] = Form.useForm();
	const [createCampaignForm] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [apiKeyStatus, setApiKeyStatus] = useState('checking');
	const [currentApiKey, setCurrentApiKey] = useState(null);
	const currentUser = useSelector((state) => state.user.currentUser);

	const companyId = currentUser?.companyId || currentUser?.company?.id;

	const {
		data: apiKeyData,
		loading: apiKeyLoading,
		error: apiKeyError,
	} = useQuery(GetSnappyAPIKey, {
		variables: { companyId },
		skip: !companyId,
		fetchPolicy: 'cache-and-network',
	});

	const {
		data: apiKeysData,
		loading: apiKeysLoading,
		error: apiKeysError,
	} = useQuery(QuerySnappyApiKeys, {
		variables: { companyId },
		skip: !companyId,
		fetchPolicy: 'cache-and-network',
	});

	useEffect(() => {
		if (apiKeyData?.getSnappyAPIKey) {
			setCurrentApiKey(apiKeyData.getSnappyAPIKey);
		}
	}, [apiKeyData]);

	useEffect(() => {
		if (apiKeyLoading || apiKeysLoading) {
			setApiKeyStatus('checking');
		} else if (apiKeyError || apiKeysError) {
			setApiKeyStatus('inactive');
		} else if (
			apiKeysData?.querySnappyApiKeys &&
			Array.isArray(apiKeysData.querySnappyApiKeys) &&
			apiKeysData.querySnappyApiKeys?.[0]?.id
		) {
			setApiKeyStatus('active');
		} else {
			setApiKeyStatus('inactive');
		}
	}, [apiKeyLoading, apiKeysLoading, apiKeyError, apiKeysError, apiKeysData]);

	useEffect(() => {
		const initializeData = async () => {
			if (!currentUser?.companyId) return;
			await getSnappyCampaigns();
			await querySnappyGrants();
		};

		initializeData();
	}, [currentUser?.companyId]);

	const querySnappyGrants = async () => {
		const companyId = currentUser?.companyId || currentUser?.company?.id;
		try {
			const { data, response } = await props.client.query({
				query: QuerySnappyGrantsByCompanyId,
				variables: {
					companyId: companyId,
					limit: 1000,
					nextToken: null,
				},
				fetchPolicy: 'network-only',
			});
			if (data?.querySnappyGrantsByCompanyId?.items) {
				setSnappyGrants(data.querySnappyGrantsByCompanyId.items);
				return data.querySnappyGrantsByCompanyId.items;
			}
			return [];
		} catch (e) {
			console.log(e);
			return [];
		}
	};

	const createSnappyGrant = async (input) => {
		try {
			const { data, response } = await props.client.mutate({
				mutation: CreateSnappyGrant,
				variables: input,
			});
			return data?.createSnappyGrant;
		} catch (e) {
			console.error('Error creating snappy grant:', e);
			return null;
		}
	};

	const deleteSnappyGrant = async (input) => {
		try {
			const { data, response } = await props.client.mutate({
				mutation: DeleteSnappyGrant,
				variables: { id: input },
			});
			return data?.createSnappyGrant;
		} catch (e) {
			console.error('Error deleting snappy grant:', e);
			return null;
		}
	};

	const updateSnappyGrant = async (input) => {
		try {
			const { data, response } = props.client.mutate({
				mutation: UpdateSnappyGrant,
				variables: {
					...input,
				},
			});
		} catch (e) {
			console.log(e);
		}
	};

	const getSnappyCampaigns = async () => {
		try {
			const response = await snappy({
				type: 'getSnappyCampaigns',
				userId: currentUser.id,
			});
			setSnappyCampaigns(response || []);
		} catch (error) {
			console.error('Error fetching snappyCampaigns:', error);
		}
	};

	const onCreateCampaign = async (values) => {
		setIsSubmitting(true);
		try {
			const payload = {
				type: 'createCampaign',
				userId: currentUser.id,
				campaignName: values.campaignName,
				snappyAPIKey: values.snappyAPIKey,
			};

			const response = await snappy(payload);

			setCampaigns([{ ...payload, id: response.id }]);

			message.success('Campaign created successfully!');
			setShowCreateCampaignForm(false);
			createCampaignForm.resetFields();
		} catch (error) {
			console.error('Error creating campaign:', error);
			message.error('Failed to create campaign.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const onDelete = async (id) => {
		try {
			await deleteSnappyGrant(id);

			setSnappyGrants((prevGrants) =>
				prevGrants.filter((grant) => grant.id !== id)
			);
			await querySnappyGrants();
			message.success('Grant deleted successfully!');
		} catch (error) {
			console.error('Error deleting snappy grant:', error);
			message.error('Failed to delete grant.');
		}
	};

	const onInlineEditSave = async (id, updatedSnappyGrant, campaignId) => {
		const costInCurrency =
			snappyCampaigns.find(
				(campaign) => campaign.id === updatedSnappyGrant.snappyCampaignId
			)?.properties?.[0]?.budget || 0;

		try {
			const company = currentUser.company;
			const snappyGrantUpdateInput = {
				input: {
					id: id,
					externalSnappyId: updatedSnappyGrant.snappyCampaignId,
					snappyCampaignId: updatedSnappyGrant.snappyCampaignId,
					snappyCampaignTierId: id,
					costInCurrency: costInCurrency,
					name: updatedSnappyGrant.name,
					description: updatedSnappyGrant.description,
					companyId: updatedSnappyGrant.companyId,
					active: true,

					allowOverBudget:
						company?.recognitionSettings?.allowAdminBalanceOverrides || false,
					allowMultipleGrants: true,
					allowForNominations: true,
					allowRevoke: true,
					requireApproval:
						company?.recognitionSettings?.requireAdminApproval || false,
					allowAssignmentNoGift: true,
					grantCategory: 'Default',
					currencyIsoCode: 'USD',
					currencySymbol: '$',
					giftExpiryDays: -1,
					remainingAssignments: -1,
					totalAssignments: 0,
					badgeIds: [],
					badgeAssignmentIds: [],
					snappyGiftIds: [],
					isHidden: false,
					isArchived: false,
					isMirroredSnappyCampaignTier: false,
					externalInformation: updatedSnappyGrant.externalInformation,
					redemptionLogs: updatedSnappyGrant.redemptionLogs,
					updatedBy: currentUser.id,
				},
			};

			await updateSnappyGrant(snappyGrantUpdateInput);

			await querySnappyGrants();

			message.success('Snappy Reward updated successfully!');
		} catch (error) {
			console.error('Error updating reward:', error);
			message.error('Failed to update Snappy Reward.');
		}
	};

	const handleUpdateCompany = async (values) => {
		try {
			const currentRecognitionSettings = {
				...currentUser.company.recognitionSettings,
				...values,
			};

			if (
				currentRecognitionSettings.tieredRewardsCampaign &&
				Array.isArray(currentRecognitionSettings.tieredRewardsCampaign)
			) {
				currentRecognitionSettings.tieredRewardsCampaign =
					currentRecognitionSettings.tieredRewardsCampaign.map((tier) => {
						const { snappyGrant, ...cleanTier } = tier;
						return cleanTier;
					});
			}

			const result = await onUpdateCompany({
				input: {
					id: currentUser?.company?.id,
					recognitionSettings: {
						...currentRecognitionSettings,
					},
				},
			});

			if (result?.data?.updateCompany) {
				updateCurrentUserCompany(result?.data?.updateCompany);
				message.success('Company updated successfully!');
			}
		} catch (error) {
			message.error('Something went wrong, please try again later');
			console.log(error);
		}
	};

	const onCreateGrant = async (grantData) => {
		const costInCurrency =
			snappyCampaigns.find(
				(campaign) => campaign.id === grantData.snappyCampaignId
			)?.properties?.[0]?.budget || 0;

		try {
			const company = currentUser.company;
			const companyId = currentUser.companyId || company?.id;

			const snappyGrantInput = {
				input: {
					externalSnappyId: grantData.snappyCampaignId,
					snappyCampaignId: grantData.snappyCampaignId,
					snappyCampaignTierId: null,
					costInCurrency: costInCurrency,
					name: grantData.name,
					description: grantData.description,
					companyId: companyId,
					active: true,

					allowOverBudget:
						company?.recognitionSettings?.allowAdminBalanceOverrides || false,
					allowMultipleGrants: true,
					allowForNominations: true,
					allowRevoke: true,
					requireApproval:
						company?.recognitionSettings?.requireAdminApproval || false,
					allowAssignmentNoGift: true,
					grantCategory: 'Default',
					currencyIsoCode: 'USD',
					currencySymbol: '$',
					giftExpiryDays: -1,
					remainingAssignments: -1,
					totalAssignments: 0,
					badgeIds: [],
					badgeAssignmentIds: [],
					snappyGiftIds: [],
					isHidden: false,
					isArchived: false,
					isMirroredSnappyCampaignTier: false,
					externalInformation: '"{}"',
					redemptionLogs: '"{}"',
					createdBy: currentUser.id,
				},
			};

			const newGrant = await createSnappyGrant(snappyGrantInput);

			if (newGrant) {
				setSnappyGrants((prevGrants) => [...prevGrants, newGrant]);
				await querySnappyGrants();
				message.success('Grant created successfully!');
			}

			return newGrant;
		} catch (error) {
			console.error('Error creating grant:', error);
			message.error('Failed to create grant.');
			return null;
		}
	};

	if (loading) {
		return <Spinner />;
	}

	return (
		<RecognitionSettingsPresentation
			currentUser={currentUser}
			handleUpdateCompany={handleUpdateCompany}
			snappyGrants={snappyGrants}
			snappyCampaigns={snappyCampaigns}
			onDelete={onDelete}
			onCreateGrant={onCreateGrant}
			onCreateCampaign={onCreateCampaign}
			showCreateCampaignForm={showCreateCampaignForm}
			setShowCreateCampaignForm={setShowCreateCampaignForm}
			onInlineEditSave={onInlineEditSave}
			form={form}
			createCampaignForm={createCampaignForm}
			apiKeyStatus={apiKeyStatus}
			currentApiKey={currentApiKey}
		/>
	);
}

const mapDispatchToProperties = (dispatch) => {
	return {
		updateCurrentUserCompany(company) {
			dispatch(userActions.updateUserCompany(company));
		},
	};
};

export default withGetCompany(
	connect(
		null,
		mapDispatchToProperties
	)(withApollo(RecognitionSettingsContainer))
);
